import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/utils";
import RadioCircle from "@/components/ui/RadioCircle";
import type { Workplace } from "@/lib/orgTree";

type Props = {
  workplaces: Workplace[];
  selectedId: string | null;
  onSelect: (id: string) => void;
};

// Grouped-by-market workplace picker: "Entire company" (root) on top, then per
// market a full-width "All of X" card followed by that market's teams in a
// 2-col radio-card grid. Selecting a node soft-highlights its whole subtree
// (access cascades down). Shared by the onboarding wizard and the admin
// assign-role dialog so both feel identical.
export default function WorkplaceListbox({ workplaces, selectedId, onSelect }: Props) {
  const [query, setQuery] = useState("");

  // A node is "included" when a selected ancestor already covers it — access
  // cascades down the org tree.
  const stateFor = (w: Workplace) => ({
    active: w.id === selectedId,
    included: selectedId !== null && w.ancestorIds.includes(selectedId),
  });

  const { companyNode, groups } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (w: Workplace) =>
      !q ||
      w.label.toLowerCase().includes(q) ||
      w.market.toLowerCase().includes(q) ||
      w.sublabel.toLowerCase().includes(q) ||
      (w.isCompany && "entire company all markets".includes(q));

    // Group by market, preserving server order (depth, position) — the market
    // node itself comes first within its group. The root sits above all groups.
    const byMarket = new Map<string, Workplace[]>();
    let company: Workplace | undefined;
    for (const w of workplaces.filter(match)) {
      if (w.isCompany) {
        company = w;
        continue;
      }
      const group = byMarket.get(w.market) ?? [];
      group.push(w);
      byMarket.set(w.market, group);
    }
    return { companyNode: company, groups: [...byMarket.entries()] };
  }, [workplaces, query]);

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter — e.g. Shopee, Wholesale…"
          className="pl-9"
          aria-label="Filter workplaces"
        />
      </div>

      <div className="space-y-5" role="listbox" aria-label="Workplaces">
        {companyNode && (
          <button
            type="button"
            role="option"
            aria-selected={companyNode.id === selectedId}
            onClick={() => onSelect(companyNode.id)}
            className={cn(
              "flex w-full items-center gap-3 rounded-md border px-4 py-3 text-left transition-colors",
              companyNode.id === selectedId
                ? "border-primary bg-primary/5"
                : "border-primary/25 bg-primary/[0.02] hover:bg-primary/5"
            )}
          >
            <RadioCircle active={companyNode.id === selectedId} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-foreground">Entire company</span>
              <span className="block text-xs text-muted-foreground">Every market and team, company-wide</span>
            </span>
          </button>
        )}
        {groups.map(([market, items]) => {
          const marketNode = items.find((w) => w.isMarket);
          const teams = items.filter((w) => !w.isMarket);
          const marketState = marketNode && stateFor(marketNode);
          return (
            <div key={market} className="space-y-2">
              <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{market}</h3>

              {marketNode && marketState && (
                <button
                  type="button"
                  role="option"
                  aria-selected={marketState.active}
                  onClick={() => onSelect(marketNode.id)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-md border px-4 py-3 text-left transition-colors",
                    marketState.active
                      ? "border-primary bg-primary/5"
                      : marketState.included
                      ? "border-primary/40 bg-primary/5"
                      : "border-primary/25 bg-primary/[0.02] hover:bg-primary/5"
                  )}
                >
                  <RadioCircle active={marketState.active} included={marketState.included} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">All of {marketNode.label}</span>
                    <span className="block text-xs text-muted-foreground">Company-wide — every team in {marketNode.label}</span>
                  </span>
                </button>
              )}

              {teams.length > 0 && (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {teams.map((w) => {
                    const { active, included } = stateFor(w);
                    return (
                      <button
                        key={w.id}
                        type="button"
                        role="option"
                        aria-selected={active}
                        onClick={() => onSelect(w.id)}
                        className={cn(
                          "flex items-center gap-3 rounded-md border px-4 py-2.5 text-left transition-colors",
                          active
                            ? "border-primary bg-primary/5"
                            : included
                            ? "border-primary/40 bg-primary/5"
                            : "border-border bg-background hover:bg-muted/50"
                        )}
                      >
                        <RadioCircle active={active} included={included} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-foreground">{w.label}</span>
                          {w.sublabel && <span className="block truncate text-xs text-muted-foreground">{w.sublabel}</span>}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {!companyNode && groups.length === 0 && (
          <p className="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
            No match. Try a shorter word — or clear the filter to see every team.
          </p>
        )}
      </div>
    </div>
  );
}
